// couponEvent.js — pure helpers behind the "สร้าง event คูปอง" screen
// (spec docs/specs/2026-09-23-coupon-event-create-design.md §3/§4).
//
// WHY THE RULES LIVE HERE AND NOT ONLY IN THE el-form RULES
//
// The server validates the same ten fields and answers 400 with a bare code
// (coupon_event_bad_prefix, …). If the FE only had element-ui rules, the two
// halves would drift the first time somebody edits one of them, and the
// operator would meet a refusal the form said nothing about. Keeping the rules
// in one pure function means the form and the server-error mapper are checked
// against the SAME source, and — because this repo cannot mount .vue files in
// jest (memory: reference-bo-fn-jest-infra) — it is the only part of the
// screen a unit test can actually reach.
//
// NOTHING IN THIS FILE MAY IMPORT VUE, VUEX OR vue-i18n. It is imported by a
// plain-JS jest spec that has no Vue runtime.

// couponEventCodes — the §3 refusal codes, as the wire spells them. Exported so
// the spec file cannot drift from the implementation by copying a typo.
export const COUPON_EVENT_CODES = {
  BAD_NAME: 'coupon_event_bad_name',
  BAD_PREFIX: 'coupon_event_bad_prefix',
  BAD_URL: 'coupon_event_bad_url',
  BAD_DATES: 'coupon_event_bad_dates',
  BAD_RUNNING: 'coupon_event_bad_running',
  BAD_DIGITS: 'coupon_event_bad_digits',
  NAME_TAKEN: 'coupon_event_name_taken',
  FORBIDDEN: 'coupon_event_forbidden',
  // §8.1 (round 2) — the two refusals DELETE /api/v1/coupon/:id can answer.
  IN_USE: 'coupon_event_in_use',
  NOT_FOUND: 'coupon_event_not_found',
};

// COUPON_EVENT_MAX_QUANTITY — the largest PRINT RUN one event may ask for:
// range × rounds, matching the BE's CouponEventMaxQuantity (§9.1).
//
// WHY THERE IS A CAP AT ALL
//
// That product is the number of rows CouponDetailCreate inserts, in a background
// goroutine, after the request has already answered 200. A typo that turns
// 10,000 into 1,000,000 is therefore not a validation error the operator can
// take back: the screen says "created", the goroutine starts an INSERT storm the
// operator cannot cancel, cannot watch and cannot see the end of, and the only
// evidence anything is wrong is the coupon count creeping up on the detail page.
// Refusing it up front is the only point at which this is cheap.
//
// Round 3 raised it from 100,000 to 200,000 and moved it off the range onto the
// product, because the range alone was never what the generator writes: an event
// of 999 numbers over 20 random rounds is 19,980 codes.
//
// The refusal reuses coupon_event_bad_running — no new wire code — because the
// operator fixes it in the running range or the round count, which is what that
// code already names.
export const COUPON_EVENT_MAX_QUANTITY = 200000;

// COUPON_EVENT_MAX_RANDOM_ROUND — §9.1's 0–200 (was 0–5). Mirrors the BE's
// couponEventMaxRandomRound; the el-input-number on the form takes its `max`
// from here so the box and the validator cannot disagree.
export const COUPON_EVENT_MAX_RANDOM_ROUND = 200;

// couponEventKnownCodes — every code couponEventErrorI18nKey will pass through
// untranslated. A code NOT on this list falls back to `unknown`, because
// showing the operator a raw wire code is the bug this mapper exists to stop.
const KNOWN_CODES = [
  COUPON_EVENT_CODES.BAD_NAME,
  COUPON_EVENT_CODES.BAD_PREFIX,
  COUPON_EVENT_CODES.BAD_URL,
  COUPON_EVENT_CODES.BAD_DATES,
  COUPON_EVENT_CODES.BAD_RUNNING,
  COUPON_EVENT_CODES.BAD_DIGITS,
  COUPON_EVENT_CODES.NAME_TAKEN,
  COUPON_EVENT_CODES.FORBIDDEN,
  COUPON_EVENT_CODES.IN_USE,
  COUPON_EVENT_CODES.NOT_FOUND,
];

// toInt returns the integer value of v, or NaN when v is not an integer.
//
// Deliberately stricter than parseInt: an el-input hands back a STRING, and
// parseInt("3 packs") is 3 — which would send a running range the operator
// never typed. "" and null are NaN here, which is what makes "required" work.
function toInt(v) {
  if (v === null || v === undefined) return NaN;
  if (typeof v === 'number') return Number.isInteger(v) ? v : NaN;
  const s = String(v).trim();
  if (s === '' || !/^[+-]?\d+$/.test(s)) return NaN;
  return parseInt(s, 10);
}

// parseDate turns the el-date-picker's `yyyy-MM-dd HH:mm:ss` string into a
// timestamp. Safari refuses "2026-01-01 00:00:00" (space instead of T), so the
// separator is normalised before Date sees it — without that this comparison
// silently passes on Chrome and fails on Safari.
function parseDate(v) {
  if (v === null || v === undefined || v === '') return NaN;
  if (v instanceof Date) return v.getTime();
  const t = Date.parse(String(v).trim().replace(' ', 'T'));
  return Number.isNaN(t) ? NaN : t;
}

// couponEventQuantity is the read-only "จำนวนคูปอง" on the form: the inclusive
// size of the running range MULTIPLIED by the number of random rounds
// (§9.1: quantity = (running_to − running_from + 1) × max(1, random_round)).
//
// The rounds argument is the round-3 change, and it is what the operator was
// missing: the generator walks the whole running range once per round, so the
// "999" the form used to show for a 999 × 20 event was a fifth of a percent of
// the codes it was about to print.
//
// Rounds are clamped to at least 1 — 0 rounds is still one pass through the
// generator's single-round branch, so a quantity of 0 would be a lie in the
// other direction.
//
// Returns 0 — never a negative number or NaN — for anything the operator has
// not finished typing, so the field reads "0" instead of "-4" or "NaN" while
// the form is half-filled. The SERVER computes the value that is stored; this
// is a preview of it.
export function couponEventQuantity(from, to, rounds) {
  const f = toInt(from);
  const t = toInt(to);
  if (Number.isNaN(f) || Number.isNaN(t)) return 0;
  if (t < f) return 0;
  return (t - f + 1) * couponEventRounds(rounds);
}

// couponEventRounds is §9.1's max(1, random_round), mirroring the BE's
// CouponEventRounds. An unreadable or half-typed value counts as one pass, for
// the same reason as above: the preview must never read 0 for a form that is
// about to create coupons.
export function couponEventRounds(rounds) {
  const r = toInt(rounds);
  if (Number.isNaN(r) || r < 1) return 1;
  return r;
}

// couponEventValidate applies §3 in the order of the §3 table and stops at the
// first failure, so a form with exactly one bad field always yields that
// field's code (acceptance §6 ข้อ 4).
//
// Returns {ok, code}; code is '' when ok.
export const COUPON_EVENT_NAME_MAX_LEN = 45;

export function couponEventValidate(form) {
  const f = form || {};

  // name — required, non-blank. Uniqueness is the server's to judge
  // (coupon_event_name_taken); it cannot be known here.
  if (typeof f.name !== 'string' || f.name.trim() === '') {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_NAME };
  }
  // …and at most 45 CHARACTERS (coupon_head.name varchar(45)); the server
  // counts runes, so a Thai name is measured the same way here.
  if (Array.from(f.name.trim()).length > COUPON_EVENT_NAME_MAX_LEN) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_NAME };
  }

  // prefix — required, 1–10 chars, [A-Z0-9]. Uppercased before the test
  // because the server uppercases it anyway: rejecting "ab" while accepting
  // "AB" would refuse a value that is about to become legal.
  const prefix = typeof f.prefix === 'string' ? f.prefix.trim().toUpperCase() : '';
  if (prefix === '' || prefix.length > 10 || !/^[A-Z0-9]+$/.test(prefix)) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_PREFIX };
  }

  // url_prefix — required, absolute http(s). A relative path or a `javascript:`
  // URL is refused: this value is baked into every generated coupon's QR text,
  // so a bad one is not one broken link but a whole print run of them.
  if (!couponEventIsHttpUrl(f.url_prefix)) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_URL };
  }

  // valid_from / valid_to — both required, valid_to strictly after valid_from.
  const from = parseDate(f.valid_from);
  const to = parseDate(f.valid_to);
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_DATES };
  }

  // running_from / running_to — integers, from ≥ 1, to ≥ from.
  const runFrom = toInt(f.running_from);
  const runTo = toInt(f.running_to);
  // running_from may be 0 (000–999 = 1,000 codes; legacy campaigns start at 0).
  if (Number.isNaN(runFrom) || Number.isNaN(runTo) || runFrom < 0 || runTo < runFrom) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_RUNNING };
  }

  // running_digit — 1–10 AND wide enough to spell running_to. A running_digit
  // of 3 with running_to 1500 does not truncate quietly: it collides, because
  // the generated codes wrap and two coupons get the same number.
  const runDigit = toInt(f.running_digit);
  if (Number.isNaN(runDigit) || runDigit < 1 || runDigit > 10) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_DIGITS };
  }
  if (runDigit < String(runTo).length) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_DIGITS };
  }

  // random_digit 0–10, random_round 0–200 (§9.1 raised the round limit from 5).
  // Zero is legal for both: it selects the old single-round PREFIX+RUNNING
  // format (BO-Coupon.go CouponDetailCreate).
  const randDigit = toInt(f.random_digit);
  if (Number.isNaN(randDigit) || randDigit < 0 || randDigit > 10) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_DIGITS };
  }
  const randRound = toInt(f.random_round);
  if (Number.isNaN(randRound) || randRound < 0 || randRound > COUPON_EVENT_MAX_RANDOM_ROUND) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_DIGITS };
  }

  // ...and the two have to agree (§9.1). A round is one pass over the running
  // range behind ONE random prefix; with no random digits there is no prefix, so
  // every pass would reprint the same numbers. The BE refuses this as
  // bad_digits, and the generator would quietly run a single pass — the operator
  // would get a third of the codes they asked for and no message at all.
  if (randDigit === 0 && randRound > 1) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_DIGITS };
  }
  // ...and d random digits can only name 10^d distinct prefixes; more rounds
  // than that cannot all get one (the BE mirrors this — its generator would
  // otherwise fail in the background after 10^d rounds with the head still
  // claiming the full count).
  if (randDigit > 0 && randRound > Math.pow(10, randDigit)) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_DIGITS };
  }

  // The cap, LAST, and on the product (§9.1) — the same position as the BE's,
  // for the same reason: range × rounds cannot be judged until both fields are
  // known to be in range, or an impossible round count would be reported as an
  // oversized range and the operator would narrow a range that was never wrong.
  if (couponEventQuantity(runFrom, runTo, randRound) > COUPON_EVENT_MAX_QUANTITY) {
    return { ok: false, code: COUPON_EVENT_CODES.BAD_RUNNING };
  }

  return { ok: true, code: '' };
}

// couponEventIsHttpUrl — exported because the el-form rule for url_prefix needs
// the same predicate without running the whole §3 sweep.
export function couponEventIsHttpUrl(v) {
  if (typeof v !== 'string' || v.trim() === '') return false;
  let u;
  try {
    u = new URL(v.trim());
  } catch (e) {
    return false;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
  // `new URL("http://")` throws, but `new URL("http:///path")` does not on
  // every engine — an empty host would produce coupon links to nowhere.
  return u.host !== '';
}

// couponEventErrorI18nKey maps a `Msg` from the API (or a code from
// couponEventValidate) onto the i18n key that names it in Thai and English.
//
// Anything unrecognised becomes `coupon_event.errors.unknown` rather than being
// passed to $t() verbatim: vue-i18n renders a missing key as the key itself, so
// the fallback is the difference between "สร้างคูปองไม่สำเร็จ" and the operator
// reading the string "coupon_event_bad_wombat" in a red box.
export function couponEventErrorI18nKey(msg) {
  return couponEventErrorI18nKeyFor(msg, 'unknown');
}

// couponEventErrorI18nKeyFor is couponEventErrorI18nKey with the FALLBACK made
// a parameter.
//
// `coupon_event.errors.unknown` reads "สร้าง event คูปองไม่สำเร็จ" — "could not
// create the coupon event". It is the right sentence on the create form and the
// wrong one everywhere else: an operator whose DELETE failed on a 500 was being
// told that a creation they never attempted did not work, which is worse than
// silence because it describes the wrong action. `fallbackKey` names the verb
// that actually failed; the KNOWN_CODES path is unchanged, because a recognised
// code already carries its own sentence.
export function couponEventErrorI18nKeyFor(msg, fallbackKey) {
  const raw = msg === null || msg === undefined ? '' : String(msg).trim();
  if (KNOWN_CODES.indexOf(raw) !== -1) {
    return 'coupon_event.errors.' + raw;
  }
  const fb = typeof fallbackKey === 'string' && fallbackKey !== '' ? fallbackKey : 'unknown';
  return 'coupon_event.errors.' + fb;
}

// ---------------------------------------------------------------------------
// Round 2 (spec §8)
// ---------------------------------------------------------------------------

// couponEventDeleteBlockedReason turns the 409's Datum ({registered, used})
// into the i18n key that tells the operator WHY the event would not delete.
//
// WHY THREE KEYS AND NOT ONE
//
// The two counters mean different things and lead to different next actions.
// "1 person has registered" is usually the deliberate safeguard the owner
// described — put a test registration on an event you do not want deleted — and
// the operator's move is to un-register it. "3 codes went out with orders" is
// not reversible at all and the operator's move is to stop. A single
// "this event is in use" would leave them guessing which of the two they are
// looking at, and the counts are already on the wire.
//
// Returns '' when NEITHER counter has moved, i.e. "nothing is blocking this" —
// so the caller can treat a falsy return as "not blocked" without re-deriving
// the rule. Anything unreadable (a missing Datum, a string where a number
// should be) counts as blocked with the generic key: on a refusal the safe
// reading is that the server had a reason, not that it had none.
export function couponEventDeleteBlockedReason(counts) {
  const c = counts || {};
  const registered = Number(c.registered);
  const used = Number(c.used);
  const hasRegistered = Number.isFinite(registered) && registered > 0;
  const hasUsed = Number.isFinite(used) && used > 0;

  // Registered wins when both block: the owner's safeguard is a registered
  // test user, so "someone registered" is the sentence that explains it —
  // and it is the one the operator can undo.
  if (hasRegistered) return 'coupon_event.errors.coupon_event_in_use_registered';
  if (hasUsed) return 'coupon_event.errors.coupon_event_in_use_used';

  // Both readable and both zero — nothing blocks.
  if (Number.isFinite(registered) && Number.isFinite(used)) return '';

  // Unreadable: the server refused, so say so generically rather than telling
  // the operator that nothing is wrong.
  return 'coupon_event.errors.coupon_event_in_use';
}

// couponAllParams builds the query for §8.2's "All" button: every code of one
// event, no running range.
//
// It exists as a function rather than an inline object literal for one reason —
// `all: 1`. The BE accepts only an explicit "1"/"true"
// (controllers.CouponEventAllRequested); `all: true` would serialise to "true"
// and work, but `all: 0`, `all: ''` or a forgotten key would silently give back
// the ordinary 1,000-row search, and the operator would Export a truncated
// spreadsheet with nothing on screen to say so. One place builds it, one test
// pins it.
//
// The running-range boxes are deliberately NOT included: §8.3 says the button
// ignores them.
export function couponAllParams(headId) {
  return { head_id: headId, all: 1 };
}

export default {
  COUPON_EVENT_CODES,
  COUPON_EVENT_MAX_QUANTITY,
  COUPON_EVENT_MAX_RANDOM_ROUND,
  couponEventQuantity,
  couponEventRounds,
  couponEventValidate,
  couponEventIsHttpUrl,
  couponEventErrorI18nKey,
  couponEventErrorI18nKeyFor,
  couponEventDeleteBlockedReason,
  couponAllParams,
};
